import React, { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, XCircle } from "lucide-react";
import { PlansMultiSelect } from "../PlansMultiSelect";
import { Field } from "./fields";
import { TID } from "../../../constants/testIds";

/** Edit form for a facility. Reports a patch (only changed fields) back to
 *  the parent so backend calls only ship the delta. */
export const EditMode = ({ facility, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(() => ({
    name: facility.name || "",
    category: facility.category || "",
    facility_type: facility.facility_type || "",
    services: facility.services || "",
    working_hours: facility.working_hours || "",
    service_days: facility.service_days || "",
    holidays: facility.holidays || "",
    phones_str: (facility.phones || []).join(", "),
    email: facility.email || "",
    region: facility.region || "",
    location: facility.location || "",
    town: facility.town || "",
    district: facility.district || "",
    maps_link: facility.maps_link ?? "",
    ghana_post_address: facility.ghana_post_address || "",
    relationship_manager: facility.relationship_manager || "",
    nmi_plans: facility.nmi_plans || "",
  }));

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const buildPatch = () => {
    const patch = {};
    const s = (v) => (typeof v === "string" ? v.trim() : v);
    const keys = [
      "name", "category", "facility_type", "services", "working_hours", "service_days",
      "holidays", "email", "region", "location", "town", "district",
      "ghana_post_address", "relationship_manager", "nmi_plans",
    ];
    keys.forEach((k) => {
      const nv = s(form[k]);
      const ov = s(facility[k] || "");
      if ((nv || "") !== (ov || "")) patch[k] = nv || null;
    });
    const newPhones = form.phones_str.split(",").map((p) => p.trim()).filter(Boolean);
    const oldPhones = facility.phones || [];
    if (JSON.stringify(newPhones) !== JSON.stringify(oldPhones)) patch.phones = newPhones;
    if ((form.maps_link ?? "") !== (facility.maps_link ?? "")) patch.maps_link = form.maps_link || null;
    return patch;
  };

  const submit = () => {
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) {
      toast.info("No changes made");
      return;
    }
    onSave(patch);
  };

  return (
    <div className="space-y-5">
      <div className="rounded-md border border-nmi-200 bg-nmi-50 p-3 text-xs text-nmi-900">
        <div className="font-bold uppercase tracking-wider mb-0.5">Edit mode</div>
        Every change is recorded in the edit history. Facility status and audit trail are never affected.
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <Field label="Name" cols={2} testId={TID.editField("name")} value={form.name} onChange={setField("name")} />
        <Field label="Facility type" testId={TID.editField("category")} value={form.category} onChange={setField("category")} />
        <Field label="Facility Type" testId={TID.editField("facility_type")} value={form.facility_type} onChange={setField("facility_type")} />
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <Field label="Region" testId={TID.editField("region")} value={form.region} onChange={setField("region")} />
        <Field label="District" testId={TID.editField("district")} value={form.district} onChange={setField("district")} />
        <Field label="Town" testId={TID.editField("town")} value={form.town} onChange={setField("town")} />
        <Field label="Location" testId={TID.editField("location")} value={form.location} onChange={setField("location")} />
        <Field label="Ghana Post" testId={TID.editField("ghana_post_address")} value={form.ghana_post_address} onChange={setField("ghana_post_address")} placeholder="GA-000-0000" />
        <div className="grid grid-cols-2 gap-2">
          <Field cols={2} label="Google Maps link" testId={TID.editField("maps_link")} value={form.maps_link} onChange={setField("maps_link")} placeholder="https://maps.app.goo.gl/..." />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <Field cols={2} label="Phones (comma-separated)" testId={TID.editField("phones")} value={form.phones_str} onChange={setField("phones_str")} placeholder="0244000000, 0302000000" />
        <Field label="Email" testId={TID.editField("email")} value={form.email} onChange={setField("email")} />
        <Field label="Relationship Manager" testId={TID.editField("relationship_manager")} value={form.relationship_manager} onChange={setField("relationship_manager")} />
      </div>

      <div className="grid grid-cols-2 gap-4 rounded-md border border-slate-200 p-4">
        <Field label="Working Hours" testId={TID.editField("working_hours")} value={form.working_hours} onChange={setField("working_hours")} placeholder="8:00AM - 5:00PM" />
        <Field label="Service Days" testId={TID.editField("service_days")} value={form.service_days} onChange={setField("service_days")} placeholder="Monday - Saturday" />
        <Field cols={2} label="Holidays" testId={TID.editField("holidays")} value={form.holidays} onChange={setField("holidays")} />
        <div className="md:col-span-2">
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">NMI Plans</label>
          <div data-testid={TID.editField("nmi_plans")}>
            <PlansMultiSelect
              value={form.nmi_plans}
              onChange={(v) => setField("nmi_plans")(v ?? "")}
              testIdPrefix="edit-plan"
            />
          </div>
        </div>
        <Field cols={2} type="textarea" label="Services" testId={TID.editField("services")} value={form.services} onChange={setField("services")} />
      </div>

      <div className="sticky bottom-0 -mx-5 flex justify-end gap-2 border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
        <button
          data-testid={TID.editCancel}
          onClick={onCancel}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          <XCircle size={14} /> Cancel
        </button>
        <button
          data-testid={TID.editSave}
          onClick={submit}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-2 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} strokeWidth={2.5} />}
          Save changes
        </button>
      </div>
    </div>
  );
};
